import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from './accessibility/FocusTrap.tsx';
import LanguageSwitcher from './LanguageSwitcher.tsx';

const SimpleHeader: React.FC = () => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useFocusTrap(mobileMenuRef, {
    isActive: isMobileMenuOpen,
    onEscape: () => setIsMobileMenuOpen(false),
    restoreFocus: true
  });

  // Navigation sections: Home, Work, Memes, Contact
  const navigationLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/projects', label: t('nav.work') },
    { path: '/memes', label: t('nav.memes') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="bg-background-card shadow-sm border-b border-green-100 fixed top-0 w-full z-50" role="banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="text-2xl font-bold text-green-600 hover:text-green-700 transition-colors"
              aria-label={`${t('companyName')} - Home`}
            >
              {t('companyName')}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center" aria-label="Main navigation">
            {navigationLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`transition-colors duration-200 font-medium relative group ${
                  location.pathname === link.path ? 'text-green-600' : 'text-text-secondary hover:text-green-600'
                }`}
                aria-current={location.pathname === link.path ? 'page' : undefined}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-green-600 transition-all duration-200 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                  aria-hidden="true"
                ></span>
              </Link>
            ))}
            <LanguageSwitcher />
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-text-secondary hover:text-green-600 p-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded-md transition-colors"
              aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={isMobileMenuOpen}
            >
              <motion.div
                animate={isMobileMenuOpen ? 'open' : 'closed'}
                className="w-6 h-6 flex flex-col justify-center items-center"
              >
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: 45, y: 6 }
                  }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="w-6 h-0.5 bg-current block mb-1.5 origin-center"
                />
                <motion.span
                  variants={{
                    closed: { opacity: 1, scaleX: 1 },
                    open: { opacity: 0, scaleX: 0 }
                  }}
                  transition={{ duration: 0.1 }}
                  className="w-6 h-0.5 bg-current block mb-1.5"
                />
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: -45, y: -6 }
                  }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="w-6 h-0.5 bg-current block origin-center"
                />
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence mode="sync">
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden backdrop-blur-sm"
              onClick={toggleMobileMenu}
              aria-hidden="true"
            />

            <motion.div
              ref={mobileMenuRef}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
              className="fixed top-0 right-0 h-full w-72 bg-background-card shadow-2xl z-50 md:hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
            >
              <div className="flex flex-col h-full">
                <div className="flex justify-end p-4 border-b border-green-100">
                  <button
                    onClick={toggleMobileMenu}
                    className="text-text-secondary hover:text-green-600 p-2 focus:outline-none focus:ring-2 focus:ring-green-500 rounded-md transition-colors"
                    aria-label={t('nav.closeMenu')}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <nav className="flex-1 overflow-y-auto py-6" aria-label="Mobile navigation">
                  <ul className="space-y-1">
                    {navigationLinks.map((link, index) => (
                      <motion.li
                        key={link.path}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.15, delay: index * 0.03, ease: 'easeOut' }}
                      >
                        <Link
                          to={link.path}
                          className={`block px-6 py-3 text-lg font-medium transition-colors duration-200 border-l-4 ${
                            location.pathname === link.path
                              ? 'text-green-600 bg-green-50 border-green-600'
                              : 'text-text-secondary hover:text-green-600 hover:bg-green-50 border-transparent hover:border-green-300'
                          }`}
                          aria-current={location.pathname === link.path ? 'page' : undefined}
                          onClick={toggleMobileMenu}
                        >
                          {link.label}
                        </Link>
                      </motion.li>
                    ))}
                  </ul>
                </nav>

                <div className="p-6 border-t border-green-100 space-y-4">
                  <div className="flex justify-center">
                    <LanguageSwitcher />
                  </div>
                  <p className="text-sm text-text-secondary text-center">
                    {t('companyName')}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default SimpleHeader;
